import { FC } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Typography } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import { GridColDef } from '@mui/x-data-grid';
import Can from 'src/components/Can';
import LinkButton from 'src/components/LinkButton';
import Table from 'src/components/Table';
import TableActions from 'src/components/Table/Actions';
import StatusIcon from 'src/components/Table/StatusIcon';
import useApiQueryPagination from 'src/hooks/useApiQueryPagination';
import { REASON_TYPE_LABEL } from 'src/modules/attendance/constants';
import { ATTENDANCE_PERMISSIONS } from 'src/modules/attendance/constants/permissions';
import attendanceApi from 'src/modules/attendance/services/api';
import ReasonFilterSchema from 'src/modules/attendance/validators/Reason/filter.schema';

import ReasonFilters from './ReasonFilters';
import { Container } from './styles';

const List: FC = () => {
  const {
    handlePageChange,
    handlePageSizeChange,
    isLoading,
    data: response,
    refetch,
  } = useApiQueryPagination(attendanceApi.admin.reasons.useReasonsQuery);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID' },
    { field: 'name', headerName: 'Nome', flex: 1 },
    {
      field: 'type',
      headerName: 'Tipo',
      headerAlign: 'center',
      align: 'center',
      valueGetter({ value }) {
        return REASON_TYPE_LABEL[value];
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      headerAlign: 'center',
      align: 'center',
      renderCell(params) {
        return <StatusIcon status={params.row.status} />;
      },
    },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      renderCell({ id, row }) {
        return (
          <TableActions
            resourceName="Motivo"
            resourceId={id}
            resourceStatus={row.status}
            editLink={`/atendimento/motivos/${id}`}
            editPermissions={[ATTENDANCE_PERMISSIONS.REASONS.UPDATE]}
            destroyApiEndpoint={attendanceApi.admin.reasons.destroyReason}
            destroyPermissions={[ATTENDANCE_PERMISSIONS.REASONS.DESTROY]}
            restoreApiEndpoint={attendanceApi.admin.reasons.restoreReason}
            restorePermissions={[ATTENDANCE_PERMISSIONS.REASONS.RESTORE]}
            descriptionPrefix={`O motivo ${row.name}`}
            onFinish={refetch}
          />
        );
      },
    },
  ];

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Motivos</Typography>

            <Can
              behavior="disable"
              permissions={[ATTENDANCE_PERMISSIONS.REASONS.STORE]}
            >
              <LinkButton
                to="/atendimento/motivos/novo"
                startIcon={<AddIcon />}
                color="primary"
                variant="contained"
                size="small"
              >
                Novo
              </LinkButton>
            </Can>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Table
                autoHeight
                rows={response?.data.data || []}
                pageSize={response?.data.meta.per_page}
                rowCount={response?.data.meta.total}
                columns={columns}
                loading={isLoading}
                pagination
                paginationMode="server"
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                canSearch
                showFilters
                filterProps={{
                  yupSchema: ReasonFilterSchema,
                  children: <ReasonFilters />,
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default List;
